import React, { useEffect, useState, useRef, Suspense } from "react";
import { JitsiMeeting } from "@jitsi/react-sdk";
import config from "common/config";
import MessageWindowHeader from "components/Chat/MessageWindowHeader";
import { messageWindowSubTitle, messageWindowTitle } from "business/channel";
import withStyles from '@mui/styles/withStyles';
import { useTranslation } from "react-i18next";
import CallIllustration from "common/assets/callIllustration.svg";
import { Prompt, useHistory, useLocation } from "react-router-dom";
import { useStore } from "react-redux";
import Consent from "components/Chat/Consent";
import { VideoFrameInvalidMessage } from "./VideoFrameInvalidMessage";

const ZoomFrame = React.lazy(() => import("components/Chat/ZoomFrame"));

const styles = {
  wrapperRestored: {
    width: "100%",
  },
  wrapperFullScreen: {
    position: "fixed",
    left: 0,
    right: 0,
    top: 0,
    bottom: 40,
  },
  videoContainerRestored: {
    flex: "1 1 auto",
    width: "100%",
    height: "60vh",
  },
  // Zoom calls take the page height left under the header instead of a fixed share of the screen.
  wrapperZoom: {
    width: "100%",
    flex: "1 1 auto",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  },
  videoContainerZoom: {
    flex: "1 1 auto",
    width: "100%",
    height: "100%",
    minHeight: 480,
  },
  videoContainerFullScreen: {
    flex: "1 1 auto",
    width: "100vw",
    height: "100%",
  },
  contentTitle: {
    fontFamily: "Roboto",
    fontSize: "24px",
    fontWeight: "bold",
    lineHeight: "120%",
    textAlign: "center",
  },
  contentSubtitle: {
    fontFamily: "Roboto",
    color: "#7E7E7E",
    fontSize: "24px",
    lineHeight: "120%",
    fontWeight: "bold",
    textAlign: "center",
    width: "50%",
  },
  videoContainerBackground: {
    zIndex: 1,
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: "#DFDFDF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
};

const interfaceConfigOverwrite = {
  APP_NAME: "MentorToGo",
  DEFAULT_REMOTE_DISPLAY_NAME: "You",
  HIDE_INVITE_MORE_HEADER: true,
  JITSI_WATERMARK_LINK: "",
  TOOLBAR_BUTTONS: [
    "microphone",
    "camera",
    "closedcaptions",
    "desktop",
    "fullscreen",
    "fodeviceselection",
    "hangup",
    "profile",
    "settings",
    "raisehand",
    "videoquality",
    "filmstrip",
    "stats",
    "shortcuts",
    "tileview",
    "download",
    "help",
    "recording",
  ],
  MEETING_NAME_ENABLED: false,
};

const configOverwrite = {
  prejoinPageEnabled: false,
};

const dateTimeOptions = {
  weekday: "long",
  year: "numeric",
  month: "long",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
  timeZone: "Asia/Kolkata",
  timeZoneName: "short",
};

// Asks before leaving a running call, in the app and on refresh or tab close.
const LeaveCallGuard = ({ message }) => {
  const store = useStore();

  useEffect(() => {
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Read from the store, not props: when the call ends, the redirect runs before
  // this re-renders, and must not ask.
  return <Prompt message={() => !store.getState().video.callInProgress || message} />;
};

const VideoFrameComponent = ({
  classes,
  displayName,
  video = {},
  callEnded,
  myId,
  isActive,
  joinVideo,
  logVideoEvent,
  submitConsent,
}) => {
  const { room = {}, channel = {}, callInProgress } = video;
  const [myJitsiParticipantId, _setMyJitsiParticipantId] = useState();
  const myJitsiParticipantIdRef = useRef(myJitsiParticipantId);
  const [recording, setRecording] = useState(false);
  const [showConsentScreen, setShowConsentScreen] = useState(true);
  const [showInvalidVisitScreen, setShowInvalidVisitScreen] = useState(false);
  const [showEarlyVisitScreen, setShowEarlyVisitScreen] = useState(false);
  const [showLateVisitScreen, setShowLateVisitScreen] = useState(false);
  const [roomDetails, setRoomDetails] = useState({});
  const [fullScreenVideo, setFullScreenVideo] = useState(false);
  const [callJustEnded, setCallJustEnded] = useState(false);

  const setMyJitsiParticipantId = (data) => {
    myJitsiParticipantIdRef.current = data;
    _setMyJitsiParticipantId(data);
  };

  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const title = messageWindowTitle(channel, myId);
  const subTitle = messageWindowSubTitle(channel, myId);

  useEffect(() => {
    if (!video?.room?.room) {
      setShowInvalidVisitScreen(true);
      callEnded();
      setShowEarlyVisitScreen(false);
      setShowLateVisitScreen(false);
      setShowConsentScreen(false);
      return;
    }

    setRoomDetails({
      ...video?.room,
      meetingLink: `${window.location.origin}/video/join-meeting/${video?.room?.room}`,
      displayTime: new Date(video?.room?.startTime)?.toLocaleString(
        "en-IN",
        dateTimeOptions
      ),
    });

    const visitStatus = video?.room?.visitStatus;
    if (visitStatus === "early") {
      setShowInvalidVisitScreen(false);
      setShowEarlyVisitScreen(true);
      setShowConsentScreen(false);
      return;
    }
    if (visitStatus === "late") {
      setShowInvalidVisitScreen(false);
      setShowLateVisitScreen(true);
      setShowConsentScreen(false);
      return;
    }

    setShowInvalidVisitScreen(false);
    setShowEarlyVisitScreen(false);
    setShowLateVisitScreen(false);
    // Zoom shows its own recording notice.
    setShowConsentScreen(video?.room?.provider !== "zoom");
  }, [isActive, video?.room?.room]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.has("room")) {
      const room = queryParams.get("room");
      queryParams.delete("room");
      history.replace({
        search: queryParams.toString(),
      });
      joinVideo(room);
    }
  }, [location, history, joinVideo]);

  useEffect(() => {
    if (!isActive) {
      setCallJustEnded(false);
    }
  }, [isActive, setCallJustEnded]);

  const onMeetingEnd = () => {
    setCallJustEnded(true);
    setMyJitsiParticipantId(null);
    callEnded();
    // Redirect to Mentorship page after call ends
    history.push('/mentorship');
  };

  const handleApiReady = (api) => {
    api.addEventListener("videoConferenceJoined", (event) => {
      const numParticipants = api.getNumberOfParticipants();

      if (!recording) {
        api.stopRecording("file");
      }

      if (numParticipants === 1 && recording) {
        api.startRecording({ mode: "file" });
      }

      setMyJitsiParticipantId(event.id);
      logVideoEvent(room.room, "join", event.id);
    });

    api.addEventListener("videoConferenceLeft", () => {
      logVideoEvent(room.room, "leave", myJitsiParticipantIdRef.current);
      onMeetingEnd();
    });
  };

  const isZoomCall = room.provider === "zoom";
  const videoContainerStyle = fullScreenVideo
    ? styles.videoContainerFullScreen
    : isZoomCall ? styles.videoContainerZoom : styles.videoContainerRestored;
  const wrapperStyle = fullScreenVideo
    ? { ...styles.wrapperFullScreen, ...(isZoomCall && { display: "flex", flexDirection: "column" }) }
    : isZoomCall ? styles.wrapperZoom : styles.wrapperRestored;

  const VideoFrameHeader = ({ titlePrefix }) => (
    <MessageWindowHeader
      titlePrefix={titlePrefix}
      windowTitle={title}
      subTitle={subTitle}
      isFullScreen={true}
      showExpandBtn={false}
      showFullScreenButton={true}
      showVideoBtn={false}
      isLoading={false}
      channel={channel}
      onFullScreenRequested={() => setFullScreenVideo(!fullScreenVideo)}
      fullScreenVideo={fullScreenVideo}
    />
  );

  if (video.error) {
    return <VideoFrameInvalidMessage type="busy" body={video.error} />;
  }

  if (showInvalidVisitScreen) {
    return <VideoFrameInvalidMessage type="invalid" />;
  }

  if (showEarlyVisitScreen) {
    return (
      <VideoFrameInvalidMessage
        type="early"
        title={roomDetails.title}
        time={roomDetails.displayTime}
        session={roomDetails.sessionName}
        link={roomDetails.meetingLink}
      />
    );
  }

  if (showLateVisitScreen) {
    return (
      <VideoFrameInvalidMessage
        type="late"
        title={roomDetails.title}
        time={roomDetails.displayTime}
        session={roomDetails.sessionName}
        link={roomDetails.meetingLink}
      />
    );
  }

  if (showConsentScreen) {
    return (
      <Consent
        submitConsent
        onConsentSubmit={(consentValue) => {
          setShowConsentScreen(false);
          if (consentValue?.key === true) {
            setRecording(true);
          }
          submitConsent(room.room, consentValue?.key === true);
        }}
      />
    );
  }

  if (callJustEnded) {
    return (
      <div style={wrapperStyle}>
        <VideoFrameHeader titlePrefix={t("Call ended with")} />
        <div style={{ ...videoContainerStyle, display: "flex", position: "relative" }}>
          <div style={{ ...videoContainerStyle, ...styles.videoContainerBackground }}>
            <p className={classes.contentTitle}>{t("callEnded")}</p>
            <p className={classes.contentSubtitle}>
              {t("toStartAnotherCallHeadOverToChatSection")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (callInProgress) {
    return (
      <div style={wrapperStyle}>
        <LeaveCallGuard message={t("leaveCallConfirm")} />
        <VideoFrameHeader titlePrefix={t("Chat with")} />
        <div style={{ ...videoContainerStyle, display: "flex", position: "relative", ...(isZoomCall && !fullScreenVideo && { marginTop: 16 }) }}>
          <div style={{ ...videoContainerStyle, ...styles.videoContainerBackground }}>
            <img src={CallIllustration} alt="" style={{ height: "25%" }} />
            <p className={classes.contentTitle}>{t("yourCallWillBeginSoon")}</p>
          </div>

          {room.provider === "zoom" && room.zoomSignature && (
            <Suspense fallback={null}>
              <ZoomFrame
                meetingNumber={room.zoomMeetingNumber}
                password={room.zoomPassword}
                signature={room.zoomSignature}
                sdkKey={room.zoomSdkKey}
                userName={displayName}
                onEnd={onMeetingEnd}
              />
            </Suspense>
          )}

          {room.provider !== "zoom" && room.room && room.token && (
            <JitsiMeeting
              domain={config.VIDEO_URL.replace(/^https?:\/\//, "")}
              roomName={room.room}
              jwt={room.token}
              userInfo={{ displayName }}
              configOverwrite={configOverwrite}
              interfaceConfigOverwrite={interfaceConfigOverwrite}
              onApiReady={handleApiReady}
              getIFrameRef={(iframeRef) => {
                iframeRef.style.position = "absolute";
                iframeRef.style.top = "0";
                iframeRef.style.left = "0";
                iframeRef.style.width = "100%";
                iframeRef.style.height = "100%";
                iframeRef.style.zIndex = 2;
              }}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <div style={{ ...videoContainerStyle, display: "flex", position: "relative" }}>
        <div style={{ ...videoContainerStyle, ...styles.videoContainerBackground }}>
          <p className={classes.contentTitle}>{t("noVideoAvailableTitle")}</p>
          <p className={classes.contentSubtitle}>{t("noVideoAvailableSubtitle")}</p>
        </div>
      </div>
    </div>
  );
};

export const VideoFrame = withStyles(styles)(VideoFrameComponent);
